import React from 'react';
import { X, AlertTriangle, UserPlus, Check } from '../../grademind/Icons';

const StudentLimitModal = ({ isOpen, onClose, limitInfo, onUpgrade, onAddStudents }) => {
    if (!isOpen) return null;

    const packageType = limitInfo?.package || 'luna';
    const isPaidPlan = !['luna', 'free', 'starter'].includes(packageType.toLowerCase());

    // Add-on rates per plan
    const addOnRates = {
        mercury: 0.80,
        neptune: 0.50,
        jupiter: 0.30
    };

    const addOnRate = addOnRates[packageType.toLowerCase()] || null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full overflow-hidden transform transition-all border border-zinc-200">
                {/* Header */}
                <div className="bg-zinc-50 border-b border-zinc-200 p-6 relative">
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 text-zinc-400 hover:text-black hover:bg-zinc-100 rounded-lg p-2 transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>

                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-zinc-100 rounded-xl flex items-center justify-center">
                            <AlertTriangle className="w-6 h-6 text-zinc-700" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-black">Plan Limit Reached</h2>
                            <p className="text-zinc-500 text-sm mt-0.5">
                                You've reached your {packageType} plan's student submission limit
                            </p>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                    {/* Current Limit Info */}
                    <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                            <AlertTriangle className="w-5 h-5 text-zinc-600 mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                                <h3 className="font-semibold text-black mb-1 text-sm">Submission Limit</h3>
                                <p className="text-sm text-zinc-600">
                                    {limitInfo?.message || 'You have reached your student submission limit for this billing period.'}
                                </p>
                                {limitInfo?.limit && (
                                    <div className="mt-3 space-y-1">
                                        <div className="flex items-center justify-between text-xs">
                                            <span className="text-zinc-500">Used this period</span>
                                            <span className="font-mono font-medium text-black">{limitInfo.current} / {limitInfo.limit}</span>
                                        </div>
                                        {limitInfo.remaining !== undefined && (
                                            <div className="flex items-center justify-between text-xs">
                                                <span className="text-zinc-500">Remaining</span>
                                                <span className="font-mono font-medium text-black">{limitInfo.remaining}</span>
                                            </div>
                                        )}
                                        {limitInfo.requested !== undefined && limitInfo.requested > 1 && (
                                            <div className="flex items-center justify-between text-xs">
                                                <span className="text-zinc-500">Trying to upload</span>
                                                <span className="font-mono font-medium text-red-600">{limitInfo.requested}</span>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Options */}
                    {isPaidPlan && addOnRate ? (
                        // Paid plan: Show add-on students option
                        <div>
                            <div className="flex items-center gap-2 mb-4">
                                <UserPlus className="w-4 h-4 text-black" />
                                <h3 className="font-semibold text-black text-sm">
                                    Add More Students
                                </h3>
                            </div>

                            <div className="space-y-4">
                                <div className="bg-white border border-zinc-200 rounded-lg p-4">
                                    <div className="flex items-center justify-between mb-3">
                                        <div>
                                            <div className="text-xs text-zinc-500 uppercase tracking-wide font-medium mb-1">
                                                Add-on Price
                                            </div>
                                            <div className="text-2xl font-bold text-black">
                                                ${addOnRate.toFixed(2)}
                                                <span className="text-sm font-normal text-zinc-500">/student</span>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => {
                                                onClose();
                                                onAddStudents?.();
                                            }}
                                            className="bg-black hover:bg-zinc-800 text-white px-5 py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 whitespace-nowrap"
                                        >
                                            <UserPlus className="w-4 h-4" />
                                            Add Students
                                        </button>
                                    </div>
                                    <p className="text-xs text-zinc-500">
                                        Purchase additional student capacity for this billing period. Add-on students reset each cycle.
                                    </p>
                                </div>

                                {/* Upgrade option */}
                                <div className="border-t border-zinc-100 pt-4">
                                    <p className="text-sm text-zinc-600 mb-3">
                                        Or upgrade your plan for more base capacity:
                                    </p>
                                    <button
                                        onClick={() => {
                                            onClose();
                                            onUpgrade?.();
                                        }}
                                        className="w-full bg-zinc-100 hover:bg-zinc-200 text-black px-4 py-2.5 rounded-lg text-sm font-medium transition-colors"
                                    >
                                        View Plans
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : (
                        // Free plan: Show upgrade option only
                        <div>
                            <div className="flex items-center gap-2 mb-4">
                                <UserPlus className="w-4 h-4 text-black" />
                                <h3 className="font-semibold text-black text-sm">
                                    Upgrade for More Students
                                </h3>
                            </div>

                            <div className="space-y-3 mb-4">
                                <div className="flex items-start gap-2.5">
                                    <div className="w-5 h-5 bg-zinc-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                        <Check className="w-3 h-3 text-black" />
                                    </div>
                                    <span className="text-sm text-zinc-600">Mercury: 25 students per period</span>
                                </div>
                                <div className="flex items-start gap-2.5">
                                    <div className="w-5 h-5 bg-zinc-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                        <Check className="w-3 h-3 text-black" />
                                    </div>
                                    <span className="text-sm text-zinc-600">Neptune: 60 students per period</span>
                                </div>
                                <div className="flex items-start gap-2.5">
                                    <div className="w-5 h-5 bg-zinc-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                        <Check className="w-3 h-3 text-black" />
                                    </div>
                                    <span className="text-sm text-zinc-600">Jupiter: 130 students per period</span>
                                </div>
                                <div className="flex items-start gap-2.5">
                                    <div className="w-5 h-5 bg-zinc-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                        <Check className="w-3 h-3 text-black" />
                                    </div>
                                    <span className="text-sm text-zinc-600">Add-on students available on paid plans</span>
                                </div>
                            </div>

                            <button
                                onClick={() => {
                                    onClose();
                                    onUpgrade?.();
                                }}
                                className="w-full bg-black hover:bg-zinc-800 text-white px-5 py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                            >
                                <UserPlus className="w-4 h-4" />
                                Upgrade Plan
                            </button>
                        </div>
                    )}

                    {/* Note */}
                    <div className="text-xs text-zinc-400 text-center pt-4 border-t border-zinc-100">
                        Student limits reset at the start of each billing period
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StudentLimitModal;
